import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { fetchCmsCollection, fetchGalleryBySlug } from '../cms';
import LightboxModal from '../components/LightboxModal';

export default function ProjectsPage() {
  const [projects, setProjects] = useState([]);
  const [galleryImages, setGalleryImages] = useState([]);
  const [lightboxOpen, setLightboxOpen] = useState(false);
  const [activeImageIndex, setActiveIndex] = useState(0);

  useEffect(() => {
    fetchCmsCollection('projects').then(data => setProjects(data));
    fetchGalleryBySlug('projekter').then(imgs => setGalleryImages(imgs));
  }, []);

  return (
    <div className="bg-[#ffffff]">
      <Helmet>
        <title>Referencer · Projekter - T.H Byg v/Jacob Hartvig Hansen</title>
        <meta name="description" content="Se vores seneste projekter og referencer inden for ståltag, tagrenovering, trapper og tilbygning i Horsens, Vejle og Rask Mølle." />
        <meta property="og:title" content="Referencer · Projekter - T.H Byg v/Jacob Hartvig Hansen" />
      </Helmet>

      {/* Hero */}
      <section className="bg-[#11161a] text-white py-16 sm:py-20">
        <div className="mx-auto max-w-7xl px-4 sm:px-8">
          <div className="max-w-3xl">
            <span className="text-xs font-black uppercase tracking-widest text-[#d92323]">
              Referencer & Galleri
            </span>
            <h1 className="text-3xl sm:text-5xl font-black text-white mt-2 tracking-tight">
              Se vores projekter
            </h1>
            <p className="mt-4 text-slate-300 text-base sm:text-lg leading-relaxed">
              Her kan du få et indblik i vores udførte opgaver med ståltag, tagrenovering, tilbygninger og tømrerarbejde for tilfredse kunder i Vejle, Horsens, Rask Mølle og Lindved.
            </p>
          </div>
        </div>
      </section>

      {/* Projects Grid */}
      <section className="py-16 sm:py-24 bg-[#f4f5f7]">
        <div className="mx-auto max-w-7xl px-4 sm:px-8">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-2 gap-8">
            {projects.map((proj, idx) => (
              <div
                key={proj.id || idx}
                className="group relative rounded-2xl overflow-hidden bg-white border border-slate-200 shadow-md hover:shadow-2xl transition-all duration-300 flex flex-col"
              >
                <div className="w-full relative h-64 sm:h-72 overflow-hidden bg-slate-900">
                  <img
                    src={proj.image && proj.image[0] ? proj.image[0] : "https://assets.auxodev.dk/assets/4aae8b48-1e4d-42c2-9336-5940e0b091a6/th-byg-16-scaled.jpg"}
                    alt={proj.title}
                    className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-105 cursor-pointer"
                    onClick={() => {
                      setActiveIndex(idx % galleryImages.length);
                      setLightboxOpen(true);
                    }}
                  />
                  {proj.location && (
                    <span className="absolute top-3 left-3 rounded-md bg-black/70 backdrop-blur-md px-3 py-1 text-xs font-bold text-white border border-white/10">
                      <i className="fa-solid fa-location-dot text-[#d92323] mr-1.5" />
                      {proj.location}
                    </span>
                  )}
                </div>

                <div className="w-full p-6 flex flex-col justify-between space-y-4 flex-1">
                  <div>
                    <span className="text-xs font-extrabold uppercase tracking-wider text-[#d92323]">
                      {proj.service_type || "Tømrerarbejde"}
                    </span>
                    <h2 className="text-xl font-bold text-slate-900 mt-1 group-hover:text-[#d92323] transition-colors">
                      {proj.title}
                    </h2>
                    <p className="mt-2 text-sm text-slate-600 leading-relaxed">
                      {proj.summary}
                    </p>
                  </div>

                  <div className="pt-2 flex items-center justify-between border-t border-slate-100">
                    <Link
                      to={`/projekter/${proj.project_slug}`}
                      className="inline-flex items-center gap-1.5 text-sm font-bold text-[#d92323] hover:text-[#b81b1b] transition-colors"
                    >
                      <span>Læs om projektet</span>
                      <i className="fa-solid fa-arrow-right text-xs" />
                    </Link>

                    <button
                      onClick={() => {
                        setActiveIndex(idx % galleryImages.length);
                        setLightboxOpen(true);
                      }}
                      className="text-xs font-semibold text-slate-500 hover:text-slate-800 transition-colors flex items-center gap-1"
                    >
                      <i className="fa-solid fa-expand" />
                      <span>Stor visning</span>
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Gallery Carousel Grid */}
      <section className="py-16 sm:py-24 bg-[#11161a] text-white">
        <div className="mx-auto max-w-7xl px-4 sm:px-8">
          <div className="text-center max-w-2xl mx-auto mb-12">
            <h2 className="text-2xl sm:text-3xl font-extrabold text-white">
              Billedgalleri – Alle Projekter
            </h2>
            <p className="mt-2 text-slate-400 text-sm">
              Klik på et billede for at åbne det i fuld skærm
            </p>
          </div>

          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-4">
            {galleryImages.map((img, idx) => (
              <div
                key={idx}
                className="group relative rounded-xl overflow-hidden bg-slate-900 border border-slate-800 shadow cursor-pointer h-44"
                onClick={() => {
                  setActiveIndex(idx);
                  setLightboxOpen(true);
                }}
              >
                <img
                  src={img}
                  alt={`Billedgalleri ${idx + 1}`}
                  className="h-full w-full object-cover transition-transform duration-300 group-hover:scale-110"
                />
                <div className="absolute inset-0 bg-black/40 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center">
                  <i className="fa-solid fa-magnifying-glass-plus text-white text-lg" />
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Lightbox Modal */}
      <LightboxModal
        images={galleryImages}
        currentIndex={activeImageIndex}
        isOpen={lightboxOpen}
        onClose={() => setLightboxOpen(false)}
        onNavigate={(idx) => setActiveIndex(idx)}
      />
    </div>
  );
}
